import axios from 'axios';
import type { Message } from '../../models/message';
import { serverMessageId } from './messageReconciliation';

export interface AdminSendAttempt {
  clientRequestId: string;
  message: string;
  workerId: string;
  sessionId: number;
  sessionGeneration: number;
  sendGeneration: number;
}

export const isAdminSendReceiptMessageForAttempt = (
  attempt: AdminSendAttempt,
  message: Message,
): boolean => {
  const expectedLogicalSendKey =
    `admin:${attempt.sessionId}:${attempt.clientRequestId}`;
  const hasRequestIdentity = Boolean(
    message.clientRequestId || message.logicalSendKey,
  );
  const clientRequestIdMatches =
    message.clientRequestId === undefined ||
    message.clientRequestId === attempt.clientRequestId;
  const logicalSendKeyMatches =
    message.logicalSendKey === undefined ||
    message.logicalSendKey === expectedLogicalSendKey;

  return hasRequestIdentity &&
    clientRequestIdMatches &&
    logicalSendKeyMatches &&
    message.workerId === attempt.workerId &&
    String(message.sessionId) === String(attempt.sessionId) &&
    message.message === attempt.message &&
    message.direction === 'OUTGOING' &&
    message.senderType === 'ADMIN';
};

export const isAdminSendDeliveryUpdateForAttempt = (
  attempt: AdminSendAttempt,
  update: {
    workerId: string;
    logicalSendKey?: string;
    clientRequestId?: string;
  },
): boolean => {
  const expectedLogicalSendKey =
    `admin:${attempt.sessionId}:${attempt.clientRequestId}`;
  const hasRequestIdentity = Boolean(
    update.clientRequestId || update.logicalSendKey,
  );

  return hasRequestIdentity &&
    update.workerId === attempt.workerId &&
    (update.clientRequestId === undefined ||
      update.clientRequestId === attempt.clientRequestId) &&
    (update.logicalSendKey === undefined ||
      update.logicalSendKey === expectedLogicalSendKey);
};

export const isAdminSendDeliveryUpdateForMessage = (
  message: Message,
  update: {
    workerId: string;
    messageId?: string;
    logicalSendKey?: string;
    clientRequestId?: string;
  },
): boolean => {
  if (message.workerId !== update.workerId) return false;
  const logicalRequestId = message.logicalSendKey?.startsWith('admin:')
    ? message.logicalSendKey.split(':').at(-1)
    : undefined;
  const requestId = message.clientRequestId ?? logicalRequestId;
  const expectedLogicalSendKey = requestId
    ? `admin:${message.sessionId}:${requestId}`
    : message.logicalSendKey;
  const messageServerId = serverMessageId(message);

  if (update.clientRequestId && requestId && update.clientRequestId !== requestId) return false;
  if (
    update.logicalSendKey &&
    expectedLogicalSendKey &&
    update.logicalSendKey !== expectedLogicalSendKey
  ) return false;
  if (update.messageId && messageServerId && update.messageId !== messageServerId) return false;

  return Boolean(
    (update.clientRequestId && requestId === update.clientRequestId) ||
    (update.logicalSendKey && expectedLogicalSendKey === update.logicalSendKey) ||
    (update.messageId && messageServerId === update.messageId),
  );
};

/**
 * A missing response or gateway/server failure cannot prove whether the backend
 * committed the send. Retrying those cases must retain the logical identity.
 */
export const isAmbiguousAdminSendFailure = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return true;
  if (!error.response) return true;
  return error.response.status === 408 || error.response.status === 429 || error.response.status >= 500;
};
